import React from 'react';
import * as types from '../constants/action-types';
import { Spaces } from '../../../../api/api-spaces';
import Calendar from '../../../../api/api-calendar';
import Bundles from '../../../../api/api-bundles';
import _ from 'lodash';
import moment from 'moment';
import { defineMessages, FormattedMessage } from 'react-intl';

const stringIds = (array) => {
  return array.map(item => `ids=${item}`).join('&');
};

const defaultErrorMessage = defineMessages({
  message: {
    id: 'hosting.calendar_sync.default_error_message',
    defaultMessage: 'Something went wrong, please try again.'
  }
});

// Call to get all of ACTIVATED host spaces
export const fetchSpaces = (SSOid, accessToken, intl) => {
  return dispatch => {
    dispatch({ type: types.CALENDAR_SPACES_FETCH_REQUEST });
    return new Promise((resolve, reject) => {
      // fetch all of user spaces
      const batchFetchSpaces = () => {
        return new Promise((resolveSpaces, rejectSpaces) => {
          const query = `owner/${SSOid}?no_deactivated=true&dropdown=true`;
          return Spaces.read(query, accessToken, (response) => {
            if (200 !== response.statusCode) {
              return rejectSpaces(intl.formatMessage(defaultErrorMessage.message));
            }

            return resolveSpaces(_.get(response, 'spaces', []));
          });
        });
      };

      const spaceFailure = (error) => {
        dispatch({ type: types.CALENDAR_SPACES_FETCH_FAILURE, error });
        return reject(error);
      };

      const spaceSuccess = (sortedArray) => {
        dispatch({
          type: types.CALENDAR_SPACES_FETCH_SUCCESS,
          spacesArray: sortedArray,
          error: ''
        });
        return resolve(sortedArray);
      };

      // start of Promise call chains
      batchFetchSpaces()
        .then(spaceSuccess)
        .catch(spaceFailure);
    });
  };
};

// sets the selected space id to app state
export const selectSpace = (selectedSpace) => (dispatch) => {
  return dispatch({
    type: types.SPACES_SELECT,
    selectedSpace,
    loadCalendarAndDayEvents: true // trigger loading of calendar and booking day info on componentDidUpdate of calendar
  });
};

// picks selected days event from events object
// if date selected does not have an event, set bookings array to []
// if date selected has an event, continue to calling boookings bundle
const selectDayEvent = () => (dispatch, getState) => {
  return new Promise((resolve) => {
    const { calendar = {} } = getState();
    const { month_events: monthEvents = {}, selectedDate = '' } = calendar;
    const date = moment(selectedDate).get('date');
    const selectedEvent = monthEvents[date];
    if (selectedEvent && selectedEvent.events.length) {
      return resolve(selectedEvent.events);
    }
    return dispatch({ type: types.RESET_DAY_EVENTS });
  });
};

// fetch information about the month events in the calendar
const fetchEvents = (spaceId, date, accessToken, intl) => dispatch => {
  return new Promise((resolve, reject) => {
    dispatch({ type: types.CALENDAR_FETCH_REQUEST });

    const fetchError = (error) => {
      dispatch({
        type: types.CALENDAR_FETCH_FAILURE,
        error: intl.formatMessage(defaultErrorMessage.message),
        loadCalendarAndDayEvents: false
      });
      return reject(error);
    };

    const fetchSuccess = (monthEvents, lastUpdated, lastUpdatedDate) => {
      dispatch({
        type: types.CALENDAR_FETCH_SUCCESS,
        month_events: monthEvents || {},
        lastUpdated,
        lastUpdatedDate,
        error: '',
        loadCalendarAndDayEvents: false
      });
      return resolve(monthEvents);
    };

    const month = moment(date).get('month') + 1;
    const year = moment(date).get('year');
    const query = `events/space/${spaceId}?year=${year}&month=${month}`;

    Calendar.readCalendar(query, accessToken, (response) => {
      const { month_events: monthEvents, error = '', statusCode = '', bucket: lastUpdated = '', last_updated: lastUpdatedDate = '' } = response;
      if (error || 400 < statusCode) {
        return fetchError(error);
      }
      return fetchSuccess(monthEvents, lastUpdated, lastUpdatedDate);
    });
  });
};

// call to get month events by space id
export const fetchCalendarEvents = (spaceId, date, accessToken, callback, intl) => {
  // callback: fetchCalendarCb in calender-main is selectDate
  return dispatch => {
    return dispatch(fetchEvents(spaceId, date, accessToken, intl))
      .then(() => callback?.(date));
  };
};

// fetch information about the day events to display in the right pane
export const fetchBookings = (events) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { auth } = getState();
    // get all booking_id from the events array
    const bookingIds = _.filter(_.map(events, 'booking_id'), item => item);
    const joinedIds = stringIds(bookingIds);
    const query = `bookings-inbox?${joinedIds}`;

    Bundles.read(query, auth.accessToken, (response) => {
      const { bookings = {}, statusCode = '' } = response;
      if (200 !== statusCode) {
        return reject(response.error);
      }
      // Create a dictionary of bookings info by booking_id
      const bookingsById = _.keyBy(bookings, (booking) => _.get(booking, ['details', 'booking_id']));
      const dayEvents = events.map((event) => {
        const bookingEvents = bookingsById[event.booking_id];
        if (bookingEvents) return _.merge({}, event, { details: bookingEvents.details });
        return event;
      });
      return resolve(dayEvents);
    });
  });
};

// when user selects a date, chain calls to:
// 1) change the date in app state
// 2) select event with selected date
// 3) fetchBookings from booking_id in the events array
// 4) collect data from fetchBookings, returns empty array if there are non events with booking_id
// 5) merge data from block day events and booking events
export const selectDate = (selectedDate) => dispatch => {
  dispatch({ type: types.DATE_SELECT, selectedDate });

  const selectDateSuccess = (mergedEvents) => {
    dispatch({
      type: types.DAY_EVENTS_FETCH_SUCCESS,
      error: '',
      day_events: { isFetching: false, events: mergedEvents },
      loadCalendarAndDayEvents: false
    });
  };

  const selectDateFailure = (intl) => {
    dispatch({
      type: types.DAY_EVENTS_FETCH_FAILURE,
      day_events: { isFetching: false, events: [] },
      error: intl.formatMessage(defaultErrorMessage.message),
      loadCalendarAndDayEvents: false
    });
  };

  dispatch(selectDayEvent())
    .then((events) => {
      dispatch({ type: types.DAY_EVENTS_FETCH_REQUEST, day_events: { isFetching: true } });
      return events;
    })
    .then((events) => {
      // if there are bookings fetch bookings else return rest of block day evetns
      const bookingIdEvents = _.some(events, 'booking_id');
      if (bookingIdEvents) return dispatch(fetchBookings(events));
      return events;
    })
    .then(selectDateSuccess)
    .catch(selectDateFailure);
};

export const postBlockDay = (payload, callback) => (dispatch, getState) => {
  // payload: object
  // callback: fn, ex: closes modal or show error message
  // no app state errors here, errors will appear in the modal
  const { auth = {} } = getState();
  dispatch({ type: types.POST_BLOCK_DAY_REQUEST });
  // POST call to block days
  const postBlockDayRequest = () => {
    return new Promise((resolvePost, rejectPost) => {
      Calendar.postBlockDay(payload, auth.accessToken, (response) => {
        const { statusCode, error, missing, events = [] } = response;
        if (200 !== statusCode) {
          callback({ error, missing });
          return rejectPost(error);
        }
        callback({ events });
        return resolvePost(events);
      });
    });
  };

  postBlockDayRequest()
    .then(() => {
      dispatch({
        type: types.POST_BLOCK_DAY_SUCCESS,
        loadCalendarAndDayEvents: true // this will trigger, on componentDidUpdate in the calendar component, a new api call to the calendar and bookings
      });
    })
    .catch(() => {
      dispatch({
        type: types.POST_BLOCK_DAY_FAILURE,
        loadCalendarAndDayEvents: false
      });
    });
};

export const deleteBlockDay = (groupId, intl) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { auth = {}, calendar } = getState();
    const { day_events: dayEvents = [] } = calendar;
    dispatch({ type: types.DELETE_BLOCK_DAY_REQUEST, error: '' });

    const deleteBlockDayCall = () => {
      return new Promise((resolveDeleteDay, rejectDeleteDay) => {
        Calendar.deleteBlockDay(groupId, auth.accessToken, (response) => {
          const { statusCode } = response;
          if (200 !== statusCode) {
            return rejectDeleteDay();
          }
          return resolveDeleteDay();
        });
      });
    };

    const removeDayFromEvents = () => {
      const filteredEvents = _.reject(dayEvents.events, event => event.group_id && event.group_id === groupId);
      return dispatch({
        type: types.DELETE_BLOCK_DAY_SUCCESS,
        error: '',
        day_events: { isFetching: false, events: filteredEvents }
      });
    };

    return deleteBlockDayCall()
      .then(() => {
        removeDayFromEvents();
        return resolve();
      })
      .catch(() => {
        dispatch({ type: types.DELETE_BLOCK_DAY_FAILURE, error: intl.formatMessage(defaultErrorMessage.message) });
        return reject();
      });
  });
};

export const fetchOpHours = (spaceId, accessToken, intl) => dispatch => {
  return new Promise((resolve, reject) => {
    dispatch({
      type: types.OPHOURS_FETCH_REQUEST
    });

    const fetchError = () => {
      dispatch({
        type: types.OPHOURS_FETCH_FAILURE,
        error: intl.formatMessage(defaultErrorMessage.message)
      });
      return reject(new Error('ophours fetch error'));
    };

    const fetchSuccess = (ophoursData) => {
      const { operatingHours, enforceSocialEventOpHours } = ophoursData;

      dispatch({
        type: types.OPHOURS_FETCH_SUCCESS,
        operating_hours: operatingHours,
        enforce_social_events_op_hours: enforceSocialEventOpHours,
        error: ''
      });
      return resolve(operatingHours);
    };

    Calendar.getOpHours(spaceId, accessToken, (response) => {
      const {
        operating_hours: operatingHours = {},
        enforce_social_events_op_hours = false,
        error = '',
        statusCode = ''
      } = response;

      if (error || 400 < statusCode) {
        return fetchError(error);
      }

      return fetchSuccess({
        operatingHours: operatingHours,
        enforceSocialEventOpHours: enforce_social_events_op_hours
      });
    });
  });
};

export const postOpHours = (spaceId, accessToken, payload) => dispatch => {
  return new Promise((resolve, reject) => {
    dispatch({
      type: types.OPHOURS_POST_REQUEST,
      isPosting: true
    });

    const fetchError = (error) => {
      dispatch({
        type: types.OPHOURS_POST_FAILURE,
        error: (<FormattedMessage id="hosting.calendar_sync.default_error_message" defaultMessage="Something went wrong, please try again."/>),
        isPosting: false
      });
      return reject(error);
    };

    const fetchSuccess = (ophours) => {
      dispatch({
        type: types.OPHOURS_POST_SUCCESS,
        operating_hours: ophours,
        error: '',
        isPosting: false
      });
      return resolve(ophours);
    };

    return Calendar.postOpHours(spaceId, accessToken, payload, (response) => {
      const {
        operating_hours: operatingHours = {},
        error = '',
        error_type,
        statusCode = ''
      } = response;

      if (error || 400 < statusCode) {
        return fetchError({ error, error_type });
      }

      return fetchSuccess(operatingHours);
    });
  });
};

export const importICSCalendarData = (spaceId, accessToken, importUrl, calendarName, cb) => {
  return dispatch => {
    Calendar.importCalendarLink(spaceId, accessToken, importUrl, calendarName, cb);
  };
};
